import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Heading, Flex, Box, Button, Progress, Text } from '@chakra-ui/react';
import { fetchSurveyData } from '../../services/fetch';
import baseService from '../../services/base';
import { useSSO } from '../../context/SSO';
import { AttachEvidenceModal, EditStatusModal } from '../../components/Modals';
import { QuestionCardSimple } from '../../components/Card';
import { DownloadResponsesButton, EvidenceDownloadButton } from '../../components/Button';
import StatusTag from '../../components/Tags/StatusTag';
import RespondentView from '../../components/Survey/RespondentView';
import { initializeDefaultValues } from './certifications/defaultValues';
import { useEvidenceDownload } from '../../hooks/useEvidenceDownload';
import { useResponsesDownload } from '../../hooks/useResponsesDownload';
import useAuditHeaderData, { useIsAudit } from '../audit/useAuditHeaderData';
import {
 respostaComConteudo,
 totalIndicadores,
 indicadoresRespondidos,
} from '../../helpers/medidasGri';

class APIError extends Error {
 constructor(message, status) {
  super(message);
  this.name = 'APIError';
  this.status = status;
 }
}

async function fetchData(url) {
 try {
  const response = await baseService.get(url);
  if (!response?.data || Object.keys(response?.data)?.length === 0) {
   throw new APIError('No data available', 404);
  }
  return response.data;
 } catch (error) {
  if (error.response) {
   throw new APIError(error.response.data.message || 'API Error', error.response.status);
  }
  throw new APIError('Network error', 500);
 }
}

export default function SimpleCertification({
 surveyId,
 companyGroupId,
 formName,
 selectedCompanyId,
 isResponsibleUser = false,
}) {
 const { user } = useSSO();
 const isAudit = useIsAudit();
 const { downloadResponses, isLoadingResponses: isDownloadingResponses } =
  useResponsesDownload();

 const [isEvidenceModalOpen, setEvidenceModalOpen] = useState(false);
 const [currentQuestionId, setCurrentQuestionId] = useState();
 const [currentFiles, setCurrentFiles] = useState([]);
 const [isLoadingResponses, setLoadingResponses] = useState(false);
 const [certificationControlId, setCertificationControlId] = useState();
 // EG-60: certificado legado (anterior ao fluxo de auditoria) não tem
 // certificate_status_control — a busca sempre volta vazia pra ele, e sem
 // este flag o efeito abaixo tentava de novo a cada revalidação do SWR
 // (`data` nas dependências), martelando o endpoint pra sempre sem motivo.
 const fetchedControlIdRef = useRef(false);
 const [isEditStatusModalOpen, setIsEditStatusModalOpen] = useState(false);
 const [isViewMode, setIsViewMode] = useState(false);

 const { downloadEvidence, isLoadingEvidence } = useEvidenceDownload();

 const { data, error: surveyError } = useSWR(
  surveyId && selectedCompanyId
   ? `survey/respondent/company?surveyId=${surveyId}&id_company=${selectedCompanyId}`
   : null,
  fetchSurveyData
 );

 const { data: statusData, mutate } = useSWR(
  certificationControlId ? `certificate-status-control/${certificationControlId}` : null,
  fetchData
 );

 async function fetchCertificationControlId(surveyId) {
  try {
   const response = await baseService.get(
    `certificate/link-certificate/certificate-status-control/${surveyId}`,
    {
     headers: {
      Accept: 'text/html,application/json',
     },
    }
   );

   return parseInt(response.data, 10);
  } catch (error) {
   console.error('Error fetching certification control ID:', error);
   throw error;
  }
 }

 // Troca de survey (sem remontar o componente) libera uma nova tentativa.
 useEffect(() => {
  fetchedControlIdRef.current = false;
 }, [surveyId]);

 useEffect(() => {
  if (surveyId && !certificationControlId && !fetchedControlIdRef.current) {
   fetchedControlIdRef.current = true;
   fetchCertificationControlId(surveyId)
    .then((id) => {
     if (!isNaN(id)) {
      setCertificationControlId(id);
     }
    })
    .catch((error) => {
     console.error('Error fetching certification control ID:', error);
    });
  }
 }, [data, surveyId, certificationControlId]);

 const currentQuestions = useMemo(() => {
  if (!data) return [];

  return data?.survey?.questions || [];
 }, [data]);

 const defaultValues = useMemo(() => {
  return currentQuestions.length ? initializeDefaultValues(currentQuestions) : {};
 }, [currentQuestions]);

 const totalQuestions = currentQuestions?.length || 0;

 // GRI-07: linha fantasma (question_response de value vazio, sem arquivo e sem
 // medida) não conta como respondida — senão o número sobe por indicador.
 const questaoRespondida = useCallback(
  (question) => {
   if (!respostaComConteudo(question, selectedCompanyId)) return false;

   if (question.has_reviewer) {
    if (question.user_responses) {
     return question.user_responses.reviewer_approved === true;
    }
    if (Array.isArray(question.users_responses)) {
     return question.users_responses.some(
      (response) =>
       response?.company?.id_company === +selectedCompanyId &&
       response.reviewer_approved === true
     );
    }
    return false;
   }

   return true;
  },
  [selectedCompanyId]
 );

 const answeredQuestionsCount = useMemo(() => {
  return currentQuestions.filter(questaoRespondida).length;
 }, [currentQuestions, questaoRespondida]);

 const answeredRequiredQuestionsCount = useMemo(() => {
  return currentQuestions.filter(
   (question) => question.is_required && questaoRespondida(question)
  ).length;
 }, [currentQuestions, questaoRespondida]);

 const requiredQuestionsCount = useMemo(() => {
  return currentQuestions.filter((question) => question.is_required).length;
 }, [currentQuestions]);

 const totalIndicators = useMemo(
  () => totalIndicadores(currentQuestions),
  [currentQuestions]
 );

 const answeredIndicators = useMemo(
  () => indicadoresRespondidos(currentQuestions, selectedCompanyId),
  [currentQuestions, selectedCompanyId]
 );

 const hasEvidenceFiles = useMemo(() => {
  return currentQuestions.some((question) =>
   question.users_responses?.some(
    (response) =>
     response?.company?.id_company === +selectedCompanyId &&
     response?.response_file?.length > 0
   )
  );
 }, [currentQuestions, selectedCompanyId]);

 const handleQuestionEvidence = useCallback((questionId, response) => {
  setCurrentQuestionId(questionId);
  setCurrentFiles(response?.response_file);
  setEvidenceModalOpen(true);
 }, []);

 const handleCloseCurrentEvidence = () => {
  setEvidenceModalOpen(false);
  setCurrentQuestionId(null);
  setCurrentFiles([]);
 };

 // EG-63: em auditoria o sub-header some e o AuditHeader único mostra progresso
 // + ações. No-op fora da auditoria.
 useAuditHeaderData(
  isAudit
   ? {
      answered: answeredQuestionsCount,
      total: totalQuestions,
      requiredAnswered: answeredRequiredQuestionsCount,
      requiredTotal: requiredQuestionsCount,
      indicatorsAnswered: answeredIndicators,
      indicatorsTotal: totalIndicators,
      downloadingResponses: isDownloadingResponses,
      downloadingEvidence: isLoadingEvidence,
      evidenceDisabled: !hasEvidenceFiles,
      onViewAsRespondent: () => setIsViewMode(true),
      onDownloadResponses: () =>
       downloadResponses({ surveyId, companyId: selectedCompanyId, companyGroupId, formName }),
      onDownloadEvidence: () => downloadEvidence({ surveyId, companyGroupId, formName }),
     }
   : null
 );

 // EG-60: sem isto, uma falha no fetch (ex.: empresa com vínculo de grupo
 // quebrado no backend) e uma questão zerada legítima caíam no mesmo "return
 // null" — a tela simplesmente sumia, sem dizer que algo deu errado.
 if (surveyError) {
  return (
   <Box bg='white' p='24px' borderRadius='12px'>
    <Text color='#B23A12' fontWeight={600} mb='4px'>
     Não foi possível carregar as questões deste certificado.
    </Text>
    <Text color='#818A8F' fontSize='sm'>
     {surveyError?.response?.data?.message ||
      surveyError?.message ||
      'Erro desconhecido.'}{' '}
     — tente recarregar a página; se persistir, avise o time técnico com esta
     mensagem.
    </Text>
   </Box>
  );
 }

 // EG-60: a busca pode terminar SEM erro nenhum (200) e ainda assim voltar
 // sem questão nenhuma — não aparece no console do navegador, só no log do
 // backend (findSurveyRespondentByCompanys loga esse caso específico). Sem
 // isto a tela ficava indistinguível de "ainda carregando".
 if (data && !currentQuestions?.length) {
  return (
   <Box bg='white' p='24px' borderRadius='12px'>
    <Text color='#B23A12' fontWeight={600} mb='4px'>
     Este certificado não tem nenhuma questão carregada.
    </Text>
    <Text color='#818A8F' fontSize='sm'>
     survey {surveyId}, empresa {selectedCompanyId} — a busca respondeu sem
     erro, mas veio vazia. Avise o time técnico com esses dois números; o log
     do backend (findSurveyRespondentByCompanys) deve ter mais detalhe.
    </Text>
   </Box>
  );
 }

 if (!data) {
  return null;
 }

 if (isViewMode) {
  return (
   <RespondentView
    defaultValues={defaultValues}
    surveyId={surveyId}
    companyId={selectedCompanyId}
    userId={user?.user?.id_user}
    questions={currentQuestions}
    companyName={currentQuestions[0]?.company?.name}
    description={formName}
    totalAnsweredQuestions={answeredQuestionsCount}
    requiredQuestionsCount={requiredQuestionsCount} 
    answeredRequiredQuestionsCount={answeredRequiredQuestionsCount} 
    onClose={() => setIsViewMode(false)}
   />
  );
 }

 return (
  <Box>
   {!isAudit && (
   <Box bg='white' p='16px' borderRadius='12px'>
    <Flex h='63px' px='16px' bg='#F9F9F9' align='center' gap='12px' mb='16px'>
     <Flex gap='12px' h='31px' align='center' ml='auto'>
      <Text fontWeight={700}>Status:</Text>
      <StatusTag status={statusData?.status || 'não enviado'} />
     </Flex>

     {!isResponsibleUser &&
      statusData?.status &&
      statusData?.status !== 'não enviado' && (
       <Button
        variant='underline'
        fontWeight={600}
        fontSize={'15px'}
        onClick={() => setIsEditStatusModalOpen(true)}
       >
        Editar status
       </Button>
      )}
    </Flex>

    {!isResponsibleUser && (
     <Flex gap='5' overflowY='hidden' pos='absolute' right='32px' top='70px'>
      <DownloadResponsesButton
       surveyId={surveyId}
       companyId={selectedCompanyId}
       companyGroupId={companyGroupId}
       formName={formName}
       isLoadingResponses={isLoadingResponses}
       setLoadingResponses={setLoadingResponses}
       height='51px'
      />
      <EvidenceDownloadButton
       isLoading={isLoadingEvidence}
       isDisabled={!hasEvidenceFiles}
       onClick={() => downloadEvidence({ surveyId, companyGroupId, formName })}
      />
     </Flex>
    )}

    <Flex h='51px' align='center' justify='space-between' gap='6'>
     <Heading as='h3' fontSize='md'>
      Questões
     </Heading>

     {!isResponsibleUser && (
      <Button
       variant='underline'
       fontWeight={600}
       ml='auto'
       onClick={() => setIsViewMode(true)}
      >
       Visualizar como respondente
      </Button>
     )}

     <Flex align='center' gap='6'>
      <Flex
       h='51px'
       align='center'
       gap='14px'
       px='20px'
       bg='rgba(0, 115, 187, 0.1)'
       borderRadius='12px'
      >
       <Text as='span' fontSize='sm'>
        Respondidas
       </Text>

       <Box height='10px' w='114px'>
        <Progress
         value={(answeredQuestionsCount / totalQuestions) * 100}
         height='10px'
         borderRadius='21px'
         bg='#E5E5E5'
        />
       </Box>
       <Text
        as='span'
        fontSize='sm'
        bg='white'
        fontWeight={600}
        p='8px 10px'
        h='35px'
        borderRadius='4px'
       >
        {answeredQuestionsCount < 10 && '0'}
        {answeredQuestionsCount || 0}/{totalQuestions < 10 && '0'}
        {totalQuestions || 0}
       </Text>
      </Flex>

      <Flex
       h='51px'
       align='center'
       gap='14px'
       px='20px'
       bg='rgba(234, 88, 43, 0.1)'
       borderRadius='12px'
      >
       <Text as='span' fontSize='sm'>
        Obrigatórias
       </Text>

       <Box height='10px' w='114px'>
        <Progress
         value={(answeredRequiredQuestionsCount / requiredQuestionsCount) * 100}
         height='10px'
         borderRadius='21px'
         bg='#E5E5E5'
         colorScheme='orange'
        />
       </Box>
       <Text
        as='span'
        fontSize='sm'
        bg='white'
        fontWeight={600}
        p='8px 10px'
        h='35px'
        borderRadius='4px'
       >
        {answeredRequiredQuestionsCount < 10 && '0'}
        {answeredRequiredQuestionsCount || 0}/ {requiredQuestionsCount < 10 && '0'}
        {requiredQuestionsCount || 0}
       </Text>
      </Flex>

      {/* GRI-07: indicador é medida da questão, não questão — contagem à parte. */}
      {totalIndicators > 0 && (
       <Flex
        h='51px'
        align='center'
        gap='14px'
        px='20px'
        bg='rgba(27, 94, 48, 0.1)'
        borderRadius='12px'
       >
        <Text as='span' fontSize='sm'>
         Indicadores
        </Text>

        <Box height='10px' w='114px'>
         <Progress
          value={(answeredIndicators / totalIndicators) * 100}
          height='10px'
          borderRadius='21px'
          bg='#E5E5E5'
          colorScheme='green'
         />
        </Box>
        <Text
         as='span'
         fontSize='sm'
         bg='white'
         fontWeight={600}
         p='8px 10px'
         h='35px'
         borderRadius='4px'
        >
         {answeredIndicators}/{totalIndicators}
        </Text>
       </Flex>
      )}
     </Flex>
    </Flex>
   </Box>
   )}

   <Box mt={isAudit ? 0 : '6'}>
    {currentQuestions?.map((question, index) => (
     <QuestionCardSimple
      key={`${question?.id_question}_${index}`}
      question={question}
      index={index}
      selectedCompanyId={selectedCompanyId}
      handleQuestionEvidence={handleQuestionEvidence}
      surveyId={surveyId}
      userId={user?.user?.id_user}
      questionnaireType='certification'
     />
    ))}
   </Box>

   <EditStatusModal
    isOpen={isEditStatusModalOpen}
    onClose={() => setIsEditStatusModalOpen(false)}
    currentStatus={statusData?.status}
    certificateId={statusData?.id}
    onStatusUpdate={() => mutate()}
    certificateStatusControlId={statusData?.id}
   />

   {currentQuestionId && (
    <AttachEvidenceModal
     isOpen={isEvidenceModalOpen}
     onClose={handleCloseCurrentEvidence}
     questionId={currentQuestionId}
     files={currentFiles}
    />
   )}
  </Box>
 );
}
